import {
  SnapshotBulkButtonComponents,
  SnapshotTableBulkName,
} from "@components/common/faro-table/faro-table-types";
import { Snapshot } from "@custom-types/base-types";
import { useShouldShowBulkAction } from "@hooks/table/use-should-show-bulk-action";
import { BulkChangeSnapshotsAccessLevel } from "@pages/project-details/project-snapshots/bulk-actions/bulk-change-snapshots-access-level";
import { BulkDeleteSnapshots } from "@pages/project-details/project-snapshots/bulk-actions/bulk-delete-snapshots";
import { BulkExportSnapshots } from "@pages/project-details/project-snapshots/bulk-actions/bulk-export-snapshots";
import {
  isFetchingProjectSnapshotsSelector,
  selectedProjectSelector,
} from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { useMemo } from "react";

interface Props {
  /** The snapshots list shown in the table */
  snapshots: Snapshot[];
}

/** List all the bulk action components for project snapshots table */
export function useSnapshotBulkActions({
  snapshots,
}: Props): SnapshotBulkButtonComponents {
  const selectedSnapshots = useAppSelector(
    selectedEntitiesSelector("snapshots")
  );
  const selectedProject = useAppSelector(selectedProjectSelector);
  const isFetchingSnapshots = useAppSelector(
    isFetchingProjectSnapshotsSelector
  );

  const isAnyRowSelected = selectedSnapshots.length !== 0;

  const allowedButtonsBasedOnRowSelection: SnapshotTableBulkName[] =
    useMemo(() => {
      // Export is always available, both for the whole list and for the selection.
      const allowedButtons: SnapshotTableBulkName[] = ["export"];

      if (isAnyRowSelected) {
        allowedButtons.push("deleteSnapshots");
        allowedButtons.push("changeSnapshotAccessLevel");
      }

      return allowedButtons;
    }, [isAnyRowSelected]);

  const { shouldShowBulkButtons } = useShouldShowBulkAction({
    allowedButtonsBasedOnRowSelection,
  });

  /** Export the selected snapshots when any is selected, otherwise the whole list */
  const snapshotsToExport = isAnyRowSelected ? selectedSnapshots : snapshots;

  // Hide the export button while snapshots are still being fetched, so the user
  // cannot trigger an export on an incomplete list.
  const shouldShowExportButton =
    shouldShowBulkButtons("export") &&
    !isFetchingSnapshots &&
    snapshotsToExport.length > 0;

  return {
    export: shouldShowExportButton ? (
      <BulkExportSnapshots
        selectedSnapshots={snapshotsToExport}
        projectName={selectedProject?.name ?? ""}
        areEntitiesSelected={isAnyRowSelected}
      />
    ) : null,

    deleteSnapshots: shouldShowBulkButtons("deleteSnapshots") ? (
      <BulkDeleteSnapshots />
    ) : null,

    changeSnapshotAccessLevel: shouldShowBulkButtons(
      "changeSnapshotAccessLevel"
    ) ? (
      <BulkChangeSnapshotsAccessLevel />
    ) : null,
  };
}
